import type { MyOffersSingleOfferResponse } from '@thirsty-camel/hump-club/src/modules/offer/offer.object'
import type CoreProductEntity from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import { backend } from '../../utils/backend'

export interface CartOffersResponse {
  validOffers: MyOffersSingleOfferResponse[]
  otherOffers: MyOffersSingleOfferResponse[]
}

export interface AutoApplyOfferInput {
  cartId: string
  phone?: string
  product: CoreProductEntity
  appliedOffers?: MyOffersSingleOfferResponse[]
}

export const fetchCartOffers = ({
  cartId,
  phone,
}: {
  cartId: string
  phone: string
}): Promise<CartOffersResponse> =>
  backend<CartOffersResponse>('cart/check-offers', {
    data: {
      phone,
      cart: cartId,
      validateOnly: true,
    },
    method: 'POST',
  })

export const applyCartOffer = ({ cartId, offerId }: { cartId: string; offerId: number }) =>
  backend('cart/apply-offer', {
    data: {
      cart: cartId,
      offer: offerId,
    },
    method: 'POST',
  })

export const findMatchingOfferForProduct = ({
  product,
  validOffers,
  appliedOffers = [],
}: {
  product: CoreProductEntity
  validOffers: MyOffersSingleOfferResponse[]
  appliedOffers?: MyOffersSingleOfferResponse[]
}) =>
  validOffers.find(offer => {
    const alreadyApplied = appliedOffers.some(
      appliedOffer => appliedOffer.offerId === offer.offerId,
    )

    if (alreadyApplied || !offer?.productBarcode?.length || !product?.barcodes?.length) {
      return false
    }

    return product.barcodes.some(barcode => offer.productBarcode.includes(barcode))
  })

export const autoApplyOffer = async ({
  cartId,
  phone,
  product,
  appliedOffers = [],
}: AutoApplyOfferInput): Promise<MyOffersSingleOfferResponse | undefined> => {
  if (!cartId || !phone) {
    return
  }

  const { validOffers } = await fetchCartOffers({ cartId, phone })

  const matchingOffer = findMatchingOfferForProduct({
    product,
    validOffers,
    appliedOffers,
  })

  if (!matchingOffer) {
    return
  }

  await applyCartOffer({ cartId, offerId: matchingOffer.offerId })

  return matchingOffer
}
